import CustomLink from "@Components/CustomLink";
import Image from "@Components/Image";
import config from "@Config/index";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import {
  AIRPORT_REDIRECT_URL,
  TAB_TO_SLUG,
} from "@Pages/AmberPlusCross/constants/amberPlusForexConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import styles from "./OffersTouchpoints.module.scss";

const iconUrl = (file: string) => `${config.IMAGE_STATIC_URL}/images/${file}`;
const crossPath = (slug?: string) => (slug ? `/plus-cross/${slug}` : "/plus-cross");

type Touchpoint = {
  id: string;
  label: string;
  caption: string;
  badge?: string;
  icon: string;
  /** Omitted for services that aren't live yet — those render as inert cells. */
  href?: string;
  comingSoon?: boolean;
};

/**
 * Paths are relative so they resolve against whichever environment is serving the page;
 * Airport Pickup is the one genuinely off-site destination and CustomLink renders it as
 * a plain anchor on its own. Forex Card intentionally lands on the plus-cross overview
 * rather than its own service tab.
 */
const TOUCHPOINTS: Touchpoint[] = [
  {
    id: "essential-kit",
    label: "Essential Kit",
    caption: "Ready before you arrive",
    badge: "Save 70%",
    icon: iconUrl("cross_essentials_kit.png"),
    href: crossPath(TAB_TO_SLUG["essential-kit"]),
  },
  {
    id: "bank",
    label: "Bank",
    caption: "Digital painless banking",
    badge: "£40 Cashback",
    icon: iconUrl("cross_bank.png"),
    href: crossPath(TAB_TO_SLUG.bank),
  },
  {
    id: "flights",
    label: "Flights",
    caption: "Exclusive MMT offers",
    badge: "Flat 7% Off",
    icon: iconUrl("flight_cross.png"),
    href: crossPath(TAB_TO_SLUG.flights),
  },
  {
    id: "forex-card",
    label: "Forex Card",
    caption: "Save on every swipe",
    icon: iconUrl("cross_forex_card.png"),
    href: crossPath(),
  },
  {
    id: "airport-pickup",
    label: "Airport Pickup",
    caption: "Arrive stress-free",
    icon: iconUrl("cross_airport.png"),
    href: AIRPORT_REDIRECT_URL,
  },
  {
    id: "sim-card",
    label: "SIM Card",
    caption: "Stay connected on arrival",
    badge: "COMING SOON",
    icon: iconUrl("cross_sim.png"),
    comingSoon: true,
  },
];

type OffersTouchpointsProps = {
  /**
   * "row" (default) is the horizontal bar that fills the content width and scrolls once the
   * six services no longer fit. "grid" is 3-up at every width, for hosts whose column is too
   * narrow for the row — Payment Success sits beside a sidebar.
   */
  layout?: "row" | "grid";
};

/**
 * The amber+ service shortcuts that sit between the offers heading and the offers rail.
 * Below md both layouts become the stacked 3-up grid.
 */
const OffersTouchpoints = ({ layout = "row" }: OffersTouchpointsProps) => {
  // Source-text keys: t() returns the key when a translation is missing, so these render
  // in English everywhere and switch to the selected language as soon as the strings are
  // added to Tolgee — no code change needed.
  const { t } = useTranslationHook();

  const handleClick = (id: string) => {
    gtagEventEmitter({
      action: "amber_plus_touchpoint_clicked",
      service: id,
    });
  };

  return (
    <div
      className={formatClassNames(
        styles.touchpoints,
        layout === "grid" && styles["touchpoints-grid"],
      )}
      data-testid="offers-touchpoints"
    >
      {TOUCHPOINTS.map(({ id, label, caption, badge, icon, href, comingSoon }) => {
        const itemClass = formatClassNames(styles.item, comingSoon && styles["item-soon"]);
        const body = (
          <>
            <Image src={icon} alt={t(label)} width={48} height={48} className={styles.icon} />
            <div className={styles.text}>
              <div className={styles["label-row"]}>
                <span className={styles.label}>{t(label)}</span>
                {badge && (
                  <span
                    className={formatClassNames(styles.badge, comingSoon && styles["badge-soon"])}
                  >
                    {t(badge)}
                  </span>
                )}
              </div>
              <span className={styles.caption}>{t(caption)}</span>
            </div>
          </>
        );

        // Not live yet: an inert cell rather than a link that goes nowhere.
        if (comingSoon || !href) {
          return (
            <div
              key={id}
              className={itemClass}
              data-testid={`offers-touchpoint-${id}`}
              aria-label={`${t(label)} — ${t("coming soon")}`}
            >
              {body}
            </div>
          );
        }

        // Opened in a new tab so the shortcuts never navigate the user away from the page
        // they were browsing. `rel` guards Airport Pickup, the one off-site destination —
        // CustomLink only forwards it on its anchor branch, which is the branch that
        // external URL takes; the internal plus-cross links don't need it.
        return (
          <CustomLink
            key={id}
            href={href}
            className={itemClass}
            dataTestId={`offers-touchpoint-${id}`}
            onClick={() => handleClick(id)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {body}
          </CustomLink>
        );
      })}
    </div>
  );
};

export default wrapperHOC(OffersTouchpoints, {
  componentName: "OffersTouchpoints",
  showForChina: true,
});
